import React, { useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useActiveFixesStore } from '../hooks/useActiveFixesStore';
import { cancelFix } from '../hooks/useFixConnection';

interface Props {
  open: boolean;
  onClose: () => void;
  filePath: string;
  onSuccess?: () => void;
}

export const FixWithClaudeDialog: React.FC<Props> = ({
  open, onClose, filePath, onSuccess
}) => {
  const { t } = useTranslation();
  const outputRef = useRef<HTMLDivElement>(null);

  // Subscribe to store for this file's fix status
  const fix = useActiveFixesStore((state) => state.getFix(filePath));
  const removeFix = useActiveFixesStore((state) => state.removeFix);

  // Auto-scroll on output changes
  useEffect(() => {
    if (fix?.output) {
      setTimeout(() => {
        outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight });
      }, 0);
    }
  }, [fix?.output]);

  // Trigger onSuccess when fix completes successfully
  useEffect(() => {
    if (fix?.status === 'success' && fix.endTime) {
      onSuccess?.();
    }
  }, [fix?.status, fix?.endTime, onSuccess]);

  const handleCancel = async () => {
    if (!fix?.processId) return;
    cancelFix(filePath, fix.processId);
    useActiveFixesStore.getState().setStatus(filePath, 'cancelled');
  };

  const handleClear = () => {
    removeFix(filePath);
    onClose();
  };

  // If no fix data, don't render
  if (!fix) {
    return null;
  }

  const isRunning = fix.status === 'running';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t('screens.devTools.fixWithClaudeDialog.title', { fileName: filePath.split('/').pop() })}
        {isRunning && (
          <Typography variant="caption" color="text.secondary" display="block">
            {t('screens.devTools.fixWithClaudeDialog.runningInBackground')}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {/* Output display */}
        <Box
          ref={outputRef}
          sx={{
            bgcolor: '#000',
            color: '#0f0',
            p: 2,
            borderRadius: 1,
            fontFamily: 'monospace',
            fontSize: '0.875rem',
            maxHeight: '400px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap'
          }}
        >
          {fix.output || t('screens.devTools.fixWithClaudeDialog.startingClaude')}
        </Box>

        {/* Git diff (after completion) */}
        {fix.gitDiff && (
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography>{t('screens.devTools.fixWithClaudeDialog.fileChangesAccordion')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  bgcolor: '#f5f5f5',
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '0.75rem',
                  fontFamily: 'monospace'
                }}
              >
                {fix.gitDiff || t('screens.devTools.fixWithClaudeDialog.noChangesDetected')}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Status message */}
        {fix.status === 'success' && (
          <Typography color="success.main" mt={2}>
            {t('screens.devTools.fixWithClaudeDialog.status.success')}
          </Typography>
        )}
        {fix.status === 'cancelled' && (
          <Typography color="warning.main" mt={2}>
            {t('screens.devTools.fixWithClaudeDialog.status.cancelled')}
          </Typography>
        )}
        {fix.status === 'error' && (
          <Typography color="error.main" mt={2}>
            {t('screens.devTools.fixWithClaudeDialog.status.error')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {isRunning ? (
          <Button onClick={handleCancel} color="error">
            {t('screens.devTools.fixWithClaudeDialog.buttons.cancel')}
          </Button>
        ) : (
          <>
            <Button onClick={handleClear}>{t('screens.devTools.fixWithClaudeDialog.buttons.clear')}</Button>
            <Button onClick={onClose} variant="contained">
              {t('screens.devTools.fixWithClaudeDialog.buttons.close')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
